/**
 * Rewarded ad flow. The client only *shows* the ad; coins are credited by the server after the ad
 * network's signed server-side callback. In development the "mock" network is simulated by the server
 * (POST /ads/mock/complete) after a minimum watch time; in the native apps the AdMob SDK is used with
 * `customData` = the session nonce so the SSV callback can be matched.
 */
import { useEffect, useState } from 'react';
import type { AdRewardSessionDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { useT } from '../i18n';
import { errorMessage } from '../lib/hooks';
import { sfx } from '../lib/sound';
import { toast } from '../store/ui';
import { useSession } from '../store/session';
import { isNative } from '../native/platform';
import { showRewardedAd } from '../native/ads';
import { Coins, Modal } from './ui';

const AD_SECONDS = 5;

export function useRewardedAd(placement: string) {
  const [session, setSession] = useState<AdRewardSessionDto | null>(null);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      setSession(await api.post<AdRewardSessionDto>('/api/v1/ads/rewarded/start', { placement }));
      void api.post('/api/v1/analytics/events', { events: [{ name: 'ad_started', props: { placement } }] }).catch(() => undefined);
    } catch (e) {
      toast(errorMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return { session, start, busy, close: () => setSession(null) };
}

export function RewardedAdPlayer({ session, onClose }: { session: AdRewardSessionDto; onClose: (credited: boolean) => void }) {
  const t = useT();
  const [left, setLeft] = useState(AD_SECONDS);
  const [state, setState] = useState<'playing' | 'verifying' | 'done' | 'failed'>('playing');
  const nativeAd = isNative && session.provider !== 'mock';
  useEffect(() => {
    if (!nativeAd) return;
    let alive = true;
    const userId = useSession.getState().user?.id ?? '';
    const before = useSession.getState().balance;
    showRewardedAd(session.customData, userId)
      .then(async (earned) => {
        if (!alive) return;
        if (!earned) return setState('failed');
        setState('verifying');
        // AdMob calls our server (SSV); wait for the credited balance to arrive over the socket.
        for (let i = 0; i < 20 && alive; i++) {
          if (useSession.getState().balance > before) return setState('done');
          await new Promise((r) => setTimeout(r, 750));
        }
        if (alive) {
          toast('Reward confirmed shortly — it will appear in your wallet.', 'info');
          setState('done');
        }
      })
      .catch(() => alive && setState('failed'));
    return () => {
      alive = false;
    };
  }, [nativeAd, session]);
  useEffect(() => {
    if (nativeAd || state !== 'playing') return;
    if (left <= 0) {
      setState('verifying');
      return;
    }
    const tm = setTimeout(() => setLeft((x) => x - 1), 1000);
    return () => clearTimeout(tm);
  }, [left, state, nativeAd]);
  useEffect(() => {
    if (nativeAd || state !== 'verifying') return;
    let alive = true;
    (async () => {
      try {
        if (session.provider === 'mock') {
          const r = await api.post<{ credited: boolean; reason?: string }>('/api/v1/ads/mock/complete', { sessionId: session.sessionId });
          if (!alive) return;
          if (r.credited) {
            sfx.coin();
            setState('done');
          } else setState('failed');
        } else {
          // Real networks credit asynchronously via SSV; the wallet updates over the socket.
          setState('done');
        }
      } catch (e) {
        if (alive) {
          toast(errorMessage(e), 'error');
          setState('failed');
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [state, session, nativeAd]);

  return (
    <Modal open onClose={() => state !== 'playing' && onClose(state === 'done')} dismissable={state !== 'playing'} title={state === 'done' ? t('ads.credited', { n: session.rewardCoins }) : t('ads.watch')}>
      <div className="center" style={{ minHeight: 200, borderRadius: 16, background: 'linear-gradient(135deg,#1e293b,#312e81)', marginBottom: 16, padding: 16, textAlign: 'center' }}>
        {state === 'playing' && (
          <div className="col" style={{ alignItems: 'center' }}>
            <div style={{ fontSize: 42 }} aria-hidden>
              📺
            </div>
            <strong>Sponsored</strong>
            <span className="dim">{t('ads.playing', { s: left })}</span>
          </div>
        )}
        {state === 'verifying' && <div className="spinner" />}
        {state === 'done' && <Coins amount={session.rewardCoins} size={40} signed />}
        {state === 'failed' && <span>{t('error.generic')}</span>}
      </div>
      <button className="btn primary block" disabled={state === 'playing' || state === 'verifying'} onClick={() => onClose(state === 'done')}>
        {t('common.close')}
      </button>
    </Modal>
  );
}
